import React from 'react';
import {
    SpaceBetween,
    BreadcrumbGroup,
    TextContent,
    Header,
    Container,
    Link,
    Alert,
    Box,
    ColumnLayout,
    Table,
    Badge,
    Tabs,
} from '@amzn/awsui-components-console';
import automationArchImage from '../../../images/automationAPIArch.png';
import '../../../styles/custom.scss';

export default function AutomationSetup() {
    const generateLink = (link, text, ext) => (
        <Link external={ext} href={link}>
            {text}
        </Link>
    );

    const V2_API_MANUAL_LINK = 'https://w.amazon.com/bin/view/DS2DeviceFarm/Documents/ReadMe/V2APIManual/';

    const apiEndpoints = [
        {
            category: 'Reservations',
            method: 'POST',
            path: '/v2/reservations/acquire',
            description: 'Acquire a device reservation by serial number or device criteria',
        },
        {
            category: 'Reservations',
            method: 'DELETE',
            path: '/v2/reservations/{reservation_id}/release',
            description: 'Release an active reservation',
        },
        {
            category: 'Reservations',
            method: 'GET',
            path: '/v2/reservations/{reservation_id}',
            description: 'Get reservation details by ID',
        },
        {
            category: 'Reservations',
            method: 'GET',
            path: '/v2/reservations',
            description: 'List reservations with optional filters',
        },
        {
            category: 'Test Runs',
            method: 'POST',
            path: '/v2/testruns/schedule',
            description: 'Schedule a DOTS-based test run using a reservation ID',
        },
        {
            category: 'Test Runs',
            method: 'GET',
            path: '/v2/testruns/{test_run_id}',
            description: 'Get test run details by ID',
        },
        {
            category: 'Test Runs',
            method: 'PUT',
            path: '/v2/testruns/{test_run_id}/cancel',
            description: 'Cancel an in-progress test run',
        },
        {
            category: 'Test Runs',
            method: 'GET',
            path: '/v2/testruns',
            description: 'List test runs with optional filters',
        },
        {
            category: 'Remote Connect',
            method: 'POST',
            path: '/v2/testruns/remote-connect',
            description: 'Establish a remote connection session using a reservation ID',
        },
        {
            category: 'Remote Connect',
            method: 'POST',
            path: '/v2/testruns/{test_run_id}/terminate-session',
            description: 'Terminate an active remote connection session',
        },
        {
            category: 'Devices',
            method: 'GET',
            path: '/v2/devices',
            description: 'List available devices with optional filters',
        },
    ];

    const methodBadgeColor = (method) => {
        switch (method) {
            case 'POST':
                return 'blue';
            case 'PUT':
                return 'blue';
            case 'GET':
                return 'green';
            case 'DELETE':
                return 'red';
            default:
                return 'grey';
        }
    };

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#/' },
                    {
                        text: 'Automation setup',
                        href: '#/automation/setup',
                    },
                ]}
            />
            <Header
                variant="h1"
                description="Programmatically reserve devices, run automated tests, and establish remote connections using the DDF V2 Automation APIs and SDKs."
            >
                Automation Setup
            </Header>

            <Alert type="info" header="V1 APIs are on the path to deprecation">
                If you are currently using the V1 Automation APIs, please plan your migration to the V2 APIs at your
                earliest convenience. If you are a new user, we recommend onboarding directly with the V2 APIs to avoid
                a future migration. See the{' '}
                <Link
                    href="#migrating-from-v1"
                    onFollow={(e) => {
                        e.preventDefault();
                        document.getElementById('migrating-from-v1')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                >
                    Migrating from V1 APIs
                </Link>{' '}
                section below for step-by-step migration guides.
            </Alert>

            {/* ===== How it works ===== */}
            <Container header={<Header variant="h2">How it works</Header>}>
                <TextContent>
                    <p>
                        The DDF V2 Automation APIs provide a <b>reservation-based</b> approach to device management that
                        eliminates the overhead of manual device coordination. Instead of manually checking device
                        availability, managing locks, or worrying about resource contention, you simply tell the API
                        what you need — and DDF handles the rest.
                    </p>
                    <p>
                        <b>Why use the Automation APIs?</b>
                    </p>
                    <ul>
                        <li>
                            <b>Scalable CI/CD integration</b> — Embed device testing directly into your Pipelines, DJS
                            jobs, EventBridge schedules, or ToD workers without worrying about setting up devices.
                        </li>
                        <li>
                            <b>Flexible device selection</b> — Reserve devices by exact serial number (DSN), or specify
                            criteria (device type, device model, device build, device pool) and let DDF automatically
                            find a matching available device.
                        </li>
                        <li>
                            <b>Two execution models</b> — Choose between DOTS-managed testing (runs on DDF
                            infrastructure) or Remote Connect (runs any framework agnostic tests on your own machine or
                            any remote terminal).
                        </li>
                        <li>
                            <b>Easier integration with SDK</b> — The Python and Java V2 SDKs provide pre-built
                            functionalities and workflows for device reservation, schedule tests, remote connect etc.
                            that help users to integrate with device farm easily.
                        </li>
                    </ul>
                </TextContent>

                <Box margin={{ top: 'l' }}>
                    <img
                        src={automationArchImage}
                        alt="DDF Automation API Architecture"
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </Box>
            </Container>

            {/* ===== API Authentication ===== */}
            <Container header={<Header variant="h2">API Authentication</Header>}>
                <TextContent>
                    <p>
                        All Automation API requests require a <b>Bearer token</b> in the <code>Authorization</code>{' '}
                        header. DDF supports two types of tokens:
                    </p>
                </TextContent>
                <ColumnLayout columns={2} variant="text-grid">
                    <SpaceBetween size="xs">
                        <Box variant="h4">Personal Token</Box>
                        <TextContent>
                            <p>
                                A token tied to your individual identity, valid for 90 days. Suitable for manual
                                testing, local scripts, and personal automation workflows.
                            </p>
                            <p>
                                Generate tokens from the{' '}
                                {generateLink('#/api-auth/personal-tokens', 'Personal Tokens', false)} page.
                            </p>
                        </TextContent>
                    </SpaceBetween>
                    <SpaceBetween size="xs">
                        <Box variant="h4">Bot Account Token</Box>
                        <TextContent>
                            <p>
                                A service-level token for automated systems, pipelines, and shared infrastructure. Not
                                tied to an individual user, making it ideal for CI/CD and long-running automation.
                            </p>
                            <p>
                                Set up bot accounts from the{' '}
                                {generateLink('#/api-auth/bot-accounts', 'Bot Accounts', false)} page.
                            </p>
                        </TextContent>
                    </SpaceBetween>
                </ColumnLayout>
                <Box margin={{ top: 's' }}>
                    <Alert type="warning" header="Security notice">
                        Do not share or distribute any tokens within or outside Amazon. Treat tokens like passwords.
                    </Alert>
                </Box>
            </Container>

            {/* ===== Getting Started ===== */}
            <Container header={<Header variant="h2">Getting started</Header>}>
                <TextContent>
                    <p>
                        We recommend using the <b>Python V2 SDK</b> or <b>Java V2 SDK</b> to interact with the
                        Automation APIs. The SDKs provide high-level workflow methods, built-in retry logic, SSM tunnel
                        management, and automatic cleanup — so you don't have to manage raw HTTP calls.
                    </p>
                    <div className="divider"></div>
                    <h3>Step 1 — Set up authentication</h3>
                    <p>
                        Generate a {generateLink('#/api-auth/personal-tokens', 'Personal Token', false)} or set up a{' '}
                        {generateLink('#/api-auth/bot-accounts', 'Bot Account', false)} for your service.
                    </p>
                    <div className="divider"></div>
                    <h3>Step 2 — Add the SDK dependency</h3>
                </TextContent>
                <Tabs
                    tabs={[
                        {
                            label: 'Python',
                            id: 'install-python',
                            content: (
                                <TextContent>
                                    <ol>
                                        <li>
                                            Add <code>DeviceFarmPythonClient-2.0</code> to your version-set.
                                        </li>
                                        <li>
                                            Add <code>DeviceFarmPythonClient-2.0</code> to the <code>dependencies</code>{' '}
                                            section in your package <code>Config</code> file.
                                        </li>
                                    </ol>
                                </TextContent>
                            ),
                        },
                        {
                            label: 'Java',
                            id: 'install-java',
                            content: (
                                <TextContent>
                                    <ol>
                                        <li>
                                            Add <code>AndroidDeviceFarmJavaClient-2.0</code> to your version-set.
                                        </li>
                                        <li>
                                            Add <code>AndroidDeviceFarmJavaClient-2.0</code> to the{' '}
                                            <code>dependencies</code> section in your package <code>Config</code> file.
                                        </li>
                                    </ol>
                                </TextContent>
                            ),
                        },
                    ]}
                />
                <TextContent>
                    <div className="divider"></div>
                    <h3>Step 3 — Reserve a device and run tests</h3>
                    <p>
                        The V2 SDKs provide pre-built methods that handle the full device lifecycle — reserve → connect
                        or schedule → release — so you can focus on your tests rather than infrastructure plumbing. It
                        also provides common functions like flashing a device, rebooting and re-connecting to device
                        etc.
                    </p>
                    <p>
                        For complete code examples, usage details, and API reference, see the SDK documentation:{' '}
                        {generateLink(
                            'https://code.amazon.com/packages/AndroidDeviceFarmJavaClient/trees/v2',
                            'Java V2 SDK',
                            true
                        )}{' '}
                        |{' '}
                        {generateLink(
                            'https://code.amazon.com/packages/DeviceFarmPythonClient/trees/v2',
                            'Python V2 SDK',
                            true
                        )}
                    </p>
                </TextContent>
            </Container>

            {/* ===== API Reference ===== */}
            <Container
                header={
                    <Header
                        variant="h2"
                        description={
                            <span>
                                For full request/response schemas and examples, see the{' '}
                                {generateLink(V2_API_MANUAL_LINK, 'V2 API Manual', true)}.
                            </span>
                        }
                    >
                        API Reference
                    </Header>
                }
            >
                <Table
                    columnDefinitions={[
                        {
                            id: 'category',
                            header: 'Category',
                            cell: (item) => item.category,
                            width: 130,
                        },
                        {
                            id: 'method',
                            header: 'Method',
                            cell: (item) => <Badge color={methodBadgeColor(item.method)}>{item.method}</Badge>,
                            width: 90,
                        },
                        {
                            id: 'path',
                            header: 'Endpoint',
                            cell: (item) => <code>{item.path}</code>,
                            width: 320,
                        },
                        {
                            id: 'description',
                            header: 'Description',
                            cell: (item) => item.description,
                        },
                    ]}
                    items={apiEndpoints}
                    variant="embedded"
                    stripedRows
                    wrapLines
                />
            </Container>

            {/* ===== API Manual & SDK Documentation ===== */}
            <Container header={<Header variant="h2">API Manual &amp; SDK Documentation</Header>}>
                <SpaceBetween size="s">
                    <TextContent>
                        <p>
                            The full API manual includes detailed request/response schemas, error codes, and additional
                            examples for every endpoint.
                        </p>
                    </TextContent>
                    <SpaceBetween size="xs">
                        {generateLink(V2_API_MANUAL_LINK, 'V2 API Manual (Wiki)', true)}
                        {generateLink(
                            'https://code.amazon.com/packages/AndroidDeviceFarmJavaClient/trees/v2',
                            'Java V2 SDK Documentation',
                            true
                        )}
                        {generateLink(
                            'https://code.amazon.com/packages/DeviceFarmPythonClient/trees/v2',
                            'Python V2 SDK Documentation',
                            true
                        )}
                    </SpaceBetween>
                </SpaceBetween>
            </Container>

            {/* ===== Migrating from V1 APIs ===== */}
            <div id="migrating-from-v1">
                <Container header={<Header variant="h2">Migrating from V1 APIs</Header>}>
                    <TextContent>
                        <p>
                            If you are currently using the V1 Automation APIs, refer to the migration guides for
                            step-by-step instructions on upgrading to V2:
                        </p>
                    </TextContent>
                    <SpaceBetween size="xs">
                        {generateLink(
                            'https://code.amazon.com/packages/AndroidDeviceFarmJavaClient/blobs/v2/--/MIGRATION_GUIDE.md',
                            'Java V1 → V2 Migration Guide',
                            true
                        )}
                        {generateLink(
                            'https://code.amazon.com/packages/DeviceFarmPythonClient/blobs/v2/--/MIGRATION_GUIDE_V1_TO_V2.md',
                            'Python V1 → V2 Migration Guide',
                            true
                        )}
                    </SpaceBetween>
                </Container>
            </div>

            {/* ===== Useful Links ===== */}
            <Container header={<Header variant="h2">Useful links</Header>}>
                <SpaceBetween vertical size="xs">
                    {generateLink(
                        'https://w.amazon.com/bin/view/DS2DeviceFarm/Documents/ReadMe/V2APIManual/#HFAQ',
                        'Automation FAQs',
                        true
                    )}
                    {generateLink(V2_API_MANUAL_LINK, 'V2 API Manual', true)}
                    {generateLink(
                        'https://wiki.labcollab.net/confluence/display/DOTS/Scheduler+User+Guide',
                        'DOTS Scheduler (KATS) user guide',
                        true
                    )}
                    {generateLink(
                        'https://wiki.labcollab.net/confluence/display/DOTS/TMS+User+Guide',
                        'DOTS Test Management Service (TMS) user guide',
                        true
                    )}
                    {generateLink(
                        'https://taskei.amazon.dev/tasks/create?template=064efa25-79cc-464f-9ac6-2026ec5a8a0d',
                        'Feature request',
                        true
                    )}
                    {generateLink(
                        'https://t.corp.amazon.com/create/templates/7b41de12-761a-40e2-98b6-c302121b351a',
                        'Bug report',
                        true
                    )}
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    );
}
